import React, { useCallback, useMemo } from 'react'
import { Box, Button, Flex, Text } from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { MatchData, Searcher } from 'fast-fuzzy'
import { ModalAsyncSelect } from 'components/ModalSelect'
import { useCompaniesQuery } from 'queries/companies'
import {
  CompanyScopesEditorProps,
  ScopeOption,
  ScopeDatesFields,
  createScopeDates,
  removeScopeAt,
  scopeFuzzyOptions,
  updateScopeAt,
} from './shared'

export const CompanyScopesCompaniesSection = ({
  value,
  onChange,
  existingScopes = [],
  errors,
}: CompanyScopesEditorProps) => {
  const { data: companies } = useCompaniesQuery()

  const existingCompanyIds = useMemo(
    () =>
      new Set(
        existingScopes
          .map((scope) => scope.companyId)
          .filter((id): id is number => id != null)
      ),
    [existingScopes]
  )

  const selectedCompanyIds = useMemo(
    () =>
      new Set(
        value
          .filter((scope) => scope.scopeType === 'company')
          .map((scope) => scope.companyId)
          .filter((id): id is number => id != null)
      ),
    [value]
  )

  const companyOptions = useMemo(() => {
    return (
      companies
        ?.filter(
          (company) =>
            !existingCompanyIds.has(company.id) &&
            !selectedCompanyIds.has(company.id)
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((company) => ({
          label: `${company.name} (${company.id})`,
          value: company.id,
        })) ?? []
    )
  }, [companies, existingCompanyIds, selectedCompanyIds])

  const companySearcher = useMemo(() => {
    return new Searcher(companyOptions, scopeFuzzyOptions)
  }, [companyOptions])

  const loadCompanyOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return Promise.resolve(companyOptions)
      const results = companySearcher.search(
        rawInput,
        scopeFuzzyOptions
      ) as unknown as MatchData<ScopeOption>[]
      return Promise.resolve(results)
    },
    [companyOptions, companySearcher]
  )

  return (
    <Box
      p={2}
      border="1px solid"
      borderColor="neutrals.100"
      borderRadius="small"
    >
      <Text fontWeight="medium" mb={2}>
        Specific companies
      </Text>
      <ModalAsyncSelect
        block
        isClearable
        isSearchable
        placeholder="Add a company"
        defaultOptions={companyOptions}
        loadOptions={loadCompanyOptions}
        value={null}
        onChange={(option) => {
          if (!option) return
          onChange([
            ...value,
            {
              scopeType: 'company',
              companyId: option.value,
              companyName: option.label,
              companyGroupId: null,
              companyGroupName: null,
              ...createScopeDates(),
            },
          ])
        }}
        aria-label="Add company scope"
      />
      <Flex flexDirection="column" gap={2} mt={3}>
        {value.map((scope, index) => {
          if (scope.scopeType !== 'company') {
            return null
          }
          return (
            <Box
              key={`company-${scope.companyId}-${index}`}
              p={2}
              border="1px solid"
              borderColor="neutrals.50"
              borderRadius="small"
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="medium">
                  {scope.companyName ?? `Company ${scope.companyId}`}
                </Text>
                <Button
                  variant="text"
                  kind="small"
                  color="red"
                  onClick={() => onChange(removeScopeAt(value, index))}
                  aria-label="Remove company scope"
                >
                  <CircleMinus size={16} />
                </Button>
              </Flex>
              <ScopeDatesFields
                scope={scope}
                index={index}
                errors={errors}
                onChange={(next) => onChange(updateScopeAt(value, index, next))}
              />
            </Box>
          )
        })}
      </Flex>
    </Box>
  )
}
